import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { resumeApplicationEnglish, resumeApplicationSpanish } from './resumeApplicationStrings';

import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

i18n
  // load translation using http -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
  // learn more: https://github.com/i18next/i18next-http-backend
  // want your translations to be loaded from a professional CDN? => https://github.com/locize/react-tutorial#step-2---use-the-locize-cdn
  .use(Backend)
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'es'],
    load: 'languageOnly',
    debug: true,
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    resources: {
      en: {
        translation: {
          resumeApplications: resumeApplicationEnglish,
          profileContent: { title: "Profile content", posts: "Posts", completed: "Completed Projects", open: "Open Projects", noCompleted: "No completed projects yet.", completedWith: "Completed with {{name}}", reviewInEscrow: "View escrow and manage your review", reviewCount_one: "review", reviewCount_other: "reviews" },
          reviews: { title: "Review your collaboration", with: "Your review of {{name}}", rating: "Your rating", comment: "Your review (optional)", outOfFive: "{{rating}} out of 5", submit: "Submit review", save: "Save review", saved: "Review saved.", loadError: "Unable to load your review.", saveError: "Unable to save your review. Please try again.", completedOnly: "Reviews are available after the escrow is completed." },
          privacy: {
            loadingProfile: "Loading profile…", signInToView: "Sign in to view this profile.",
            privateProfile: "This profile is private.", profileUnavailable: "This profile is unavailable.",
            ownProfileOnly: "You can only view your own profile.",
          },
          notificationPreferences: {
            title: "Email notifications", marketplace: "Escrow and milestone updates",
            language: "Email language", enabled: "Send me email updates", disabled: "No email updates",
            loading: "Loading email preferences…", loadError: "Could not load email preferences.",
            retry: "Try again", save: "Save email preferences", saving: "Saving…",
            saved: "Email preferences saved.", saveError: "Could not save email preferences. Please try again.",
          },
          reactions: {
            reactions: "Reactions",
            like: "Like",
            love: "Love",
            applause: "Applause",
            laughing: "Laughing",
            wow: "Wow",
            highfive: "High Five!"
          },
          story: {
            upload: "Upload a Story",
            desc: "Stories are visible for 24 hours before they disappear!",
            choose: "Choose image",
            error: "Stories must be image only (no .mp4, .mp3, .mov files)",
            post: "Post"
          },
          categories: {
            all: "All",
            category: "Category",
            general: "General",
            jobs: "Jobs",
            events: "Events",
            news: "News",
            newsArticles: "News and Articles",
            articles: "Articles",
            local: "Local",
            us: "United States",
            latam: "Latin America",
            global: "Global",
            tamu: "Texas A&M",
            games: "Aggie Games",
            advice: "Advice",
            fans: "Aggie Fans",
            greatThings: "Great Things",
            construction: "Construction",
            free: "Free Items",
            home: "Home",
            landscape: "Landscape",
            property: "Property Rental",
            vehicle: "Vehicles",
            deals: "Aggie Deals",
          },
          share: {
            create: "Howdy! Create a new post...",
            createAd: "Create new ad...",
            sellItem: "Sell a new item...",
            select: "Select Category *",
            pleaseSelect: "Please select a category.",
            add: "Add image/video/audio",
            gif: "Add GIF",
            flag: "Add TAMU flag",
            post: "Post",
            uploading: "Uploading...",
            error: "Videos must be shorter than 60 seconds. Audio must be shorter than 60 seconds.",
            ten: "You can only select up to 10 files.",
            addMore: "Add more +",
            guest: "Howdy! To upload posts, please sign in or make an account.",
            url: "Add URL",
            confirmationHeading: "Upload was successful!",
            confirmationText: "Thank you for your contribution.",
            processingHeading: "Processing",
            processingText: "Thank you for your patience. This process may take up to 1 minute.",
            submitMore: "Share another",
          },
          sections: {
            topPosts: "Most Liked Posts",
            home: "Home",
            discover: "Discover",
            friends: "Friends",
            postAd: "Post an Ad",
            shorts : "Shorts",
          },
          common: {
            back: "Back",
            cancel: "Cancel",
          },
          post: {
            comment: " Comment",
            share: "Share",
            write: "Write a comment...",
            send: "Send",
            rate: "rate this comment"
          },
          shorts: {
            desc: "Share short videos to have a laugh and stay updated.",
            post: "Upload a video shorter than 30 seconds.",
            caption: "Add a caption...",
            guest: "Howdy! To upload shorts, please login or make an account.",
          },
          update: {
            welcome: "Welcome to Poststation!",
            setupDesc: "To get started, let's fill out your profile.",
            start: "Get Started",
            optional: "All fields are optional",
            cover: "Cover Picture",
            profilePic: "Profile Picture",
            email: "Email",
            phone: "Phone Number",
            password: "Password",
            name: "Name",
            bio: "Bio",
            country: "Country/City",
            language: "Language",
            website: "Website",
            link: "Link",
            msg: "Your profile is visible to all platform participants.",
            update: "Update",
            updating: "Updating",
            submit: "Update Your Profile",
            skills: "Skills",
            skillsPlaceholder: "e.g. Graphic Design, Python, Marketing",
            addSkill: "Add",
            university: "University",
            selectUniversity: "Select university",
            major: "Major",
            gradYear: "Graduation Year",
            gradYearPlaceholder: "e.g. 2026",
            orgName: "Organization Name",
            orgNamePlaceholder: "Organization or business name",
            orgType: "Organization Type",
            selectOrgType: "Select type",
            orgTypeBusiness: "Business",
            orgTypeNonprofit: "Non-profit",
            orgTypeResident: "Resident",
          },
          users: {
            build: "Build Your Community",
            following: "Following",
            follow: "Follow",
            followers: "Followers",
            all: "All Users",
            search: "Search"
          },
          tamu: {
            tamu: "Texas A&M University",
            desc: "Stay connected with your fellow Aggies.",
            filter: "Filter Posts by Category",
            aggieMeals: "Aggie Meals",
            aggieConsulting: "Aggie Consulting",
            home: "Home",
          },
          meals: {
            title: "Aggie Meals",
            tagline: "Weekly deals for students around Bryan-College Station",
            time: "Time:",
            location: "Location:",
          },
          consulting: {
            title: "Aggie Consulting",
            tagline: "Short-term projects for students to build experience",
            compensation: "Comp:",
            timeframe: "Timeline:",
            location: "Location:",
          },
          otherPages: {
            greatThings: "Share and discover fun content to connect with other users.",
            news: "Stay up-to-date with the latest news.",
            filterNews: "Filter News by Category",
            market: "Post advertisements, buy and sell goods, and find great deals near you!",
            postAd: "Post an ad for your business to appear in other users' social feed."
          },
          jobs: {
            desc: "Find a job or post a job opening for your business.",
            find: "Find a job",
            select: "Select",
            post: "Post a Job",
            filter: "Filter Jobs by Category",
            type: "Job type*",
            name: "Job name*",
            pay: "Pay*",
            schedule: "Schedule*",
            location: "Location",
            description: "Description",
            contact: "To apply, contact: ",
            gardening: "Gardening",
            house: "Housekeeping",
            janitor: "Janitorial Service",
            restaurant: "Restaurant",
            students: "Students",
            professionals: "Professionals",
            temporary: "Temporary",
            office: "Office",
            sales: "Sales",
            delete: "delete job",
            error: "* indicates a required field"
          },
          notifs: {
            comment: "commented on your post.",
            follow: "followed you!",
            reaction: "reacted ",
            topost: " to your post.",
            notifs: "Notifications",
            today: "Today",
            week: "This week",
            earlier: "Earlier",
            escrow_invited: "invited you to an escrow.",
            student_accepted: "accepted your escrow invitation.",
            student_declined: "declined your escrow invitation.",
            milestone_added: "added a new milestone to your escrow.",
            milestone_approved: "approved your milestone submission.",
            change_requested: "requested changes on a milestone.",
            artifact_submitted: "submitted a deliverable.",
            escrow_completed: "marked the escrow as complete.",
            escrow_reopened: "sent the escrow back for revision.",
          },
          events: {
            desc: "Advertise your event or find plans for tonight!",
            submit: "Submit an Event",
            name: "Event name*",
            date: "Date*",
            time: "Time*",
            location: "Location*",
            description: "Description",
            url: "URL",
            upcoming: "Upcoming Events",
            error: "Please fill in the following fields: ",
            category: "Category"
          },
          login: {
            login: "Login",
            desc: "A platform for the Brazos Valley community and the next generation of Aggie talent build something together!",
            dont: "Don't have an account?",
            register: "Register",
            username: "Username",
            invalid: "Invalid username or password",
            error: "Can't login!"
          },
          register: {
            desc: "Poststation connects Brazos Valley students with local businesses, non-profits, and residents for project-based work.",
            do: "Do you have an account?",
            question: "What type of account?",
            student: "Student",
            studentDesc: "Texas A&M or Blinn College student looking for real-world project experience.",
            local: "Project Provider",
            localDesc: "Individual, company, or organization offering projects to students.",
            terms: "Note: By clicking `Register` below, you agree to the ",
            privacy: "Privacy Policy",
            use: "Terms of Use.",
            and: "and",
            back: "Go Back",
            businessName: "Business name",
            skills: "Skills",
            services: "Services",
            selectServices: "Select services",
            servicesUnavailable: "Services are unavailable right now.",
            confirm: "Confirm password",
            error: "All fields are required.",
            password: "Password must contain at least one lowercase letter, one uppercase letter, one digit, and be between 8 and 24 characters long.",
            match: "Passwords don't match.",
            email: "Invalid email address.",
            exists: "An account with this email already exists."
          },
          provider: {
            showPassword: "Show password", hidePassword: "Hide password",
            details: "Project Provider details",
            type: "Provider type",
            selectType: "Select a provider type",
            contactPerson: "Contact person",
            contactPersonPlaceholder: "Full name of the person responsible for this account",
            companyName: "Company name",
            companyNamePlaceholder: "Legal or commonly used company name",
            organizationName: "Organization name",
            organizationNamePlaceholder: "School, nonprofit, community group, or organization name",
            contactLabel: "Contact: {{name}}",
            requiredSetup: "Provider details are required before you can post a project.",
            saving: "Saving…",
            completeProfile: "Complete your Project Provider profile before posting a project.",
            types: { individual: "Individual", company: "Company", organization: "Organization" },
            descriptions: { individual: "A person offering a project on their own behalf.", company: "A commercial business offering a project.", organization: "A nonprofit, school, community group, government entity, or other noncommercial organization." },
            errors: {
              provider_type_required: "Choose Individual, Company, or Organization.",
              contact_person_required: "Contact person name is required.",
              company_name_required: "Company name is required.",
              organization_name_required: "Organization name is required.",
              provider_field_too_long: "Contact names must be at most 150 characters and entity names at most 200.",
              provider_fields_conflict: "Only enter the entity name for the selected provider type.",
              typeRequired: "Choose Individual, Company, or Organization.",
              contactRequired: "Contact person name is required.",
              companyRequired: "Company name is required.",
              organizationRequired: "Organization name is required.",
              saveFailed: "Unable to save provider details. Please try again."
            }
          },
          admin: {
            dashboard: "Admin Dashboard",
            users: "Users",
            escrows: "Escrows",
            allTypes: "All Types",
            allStatuses: "All Statuses",
            name: "Name",
            email: "Email",
            type: "Type",
            joined: "Joined",
            project: "Project",
            student: "Student",
            local: "Project Provider",
            status: "Status",
            lastUpdated: "Last Updated",
            escrowLock: "Create Match (Escrow Lock)",
            selectStudent: "Select a student",
            selectProject: "Select a project",
            lockBtn: "Create Escrow",
            lockSuccess: "Escrow created successfully.",
            lockError: "Failed to create escrow.",
            noUsers: "No users found.",
            noEscrows: "No escrows found.",
          },
          escrow: {
            milestones: "Milestones", activity: "Activity", toggleActivity: "Toggle activity feed", noMilestones: "No milestones yet.", milestoneLoadError: "Unable to load milestones.",
            accept: "Accept Invitation",
            decline: "Decline",
            pendingMsg: "You have been invited to this project. Accept to begin work.",
            invite: "Invite to Project",
            inviteTitle: "Invite Student",
            selectProject: "Select one of your open projects",
            noProjects: "You have no open projects. Create a project first.",
            inviteSuccess: "Invitation sent! Escrow created.",
            inviteError: "Failed to create escrow.",
            confirm: "Send Invitation",
            myEscrows: "My Escrows",
            noEscrows: "No escrows yet.",
            status: "Status",
            pending: "Pending",
            active: "Active",
            submitted: "Submitted",
            completed: "Completed",
            cancelled: "Cancelled",
            project: "Project",
            student: "Student",
            local: "Project Provider",
            lastUpdated: "Last updated",
            submitDeliverable: "Submit Deliverable",
            descriptionPlaceholder: "Describe your submission...",
            submitBtn: "Submit Work",
            submitting: "Submitting...",
            approve: "Approve & Complete",
            requestChanges: "Request Changes",
            artifact: "Submitted Work",
            timeline: "Status Timeline",
            backToEscrows: "Back to My Escrows",
            viewProject: "View Project",
            submitError: "Please add a description before submitting.",
            submitSuccess: "Work submitted successfully.",
            noArtifact: "No deliverable submitted yet.",
          },
          services: {
            category: "Category",
            selectCategory: "Select a category",
            titleField: "Service title*",
            descField: "Description*",
            skillsPlaceholder: "Skills this covers",
            availabilityField: "Availability",
            availabilityPlaceholder: "e.g. Weekends, 10 hrs/week",
            error: "Title and description are required.",
            post: "Post a Service",
            edit: "Edit Service",
            save: "Save Changes",
            posted: "Service posted!",
            updated: "Service updated!",
          },
          talent: {
            browse: "Browse Talent",
            studentTalent: "Student Talent",
            studentServices: "Student Services",
            students: "Students",
            services: "Services",
            searchPlaceholder: "Search by skill or name...",
            filterServices: "Filter services by category",
            allCategories: "All categories",
            categoriesSelected: "{{count}} categories selected",
            scrollCategoriesLeft: "Scroll categories left",
            scrollCategoriesRight: "Scroll categories right",
            loading: "Loading…", loadError: "Unable to load students.",
            noStudents: "No students found.",
            noServices: "No services listed yet.",
            noServicesInCategory: "No services in this category yet.",
            postService: "Post a Service",
            editService: "Edit Service",
            viewProfile: "View Profile",
            deleteService: "Delete Service",
            deleteConfirm: "Are you sure you want to delete this service? This cannot be undone.",
          },
          projects: {
            browse: "Browse Projects",
            all: "All", loading: "Loading…", loadError: "Unable to load projects.", notFound: "Project not found.",
            viewProviderProjects: "View projects", clearProvider: "Show all providers", providerFilter: "Projects by {{name}}", closeConfirm: "Close this project? Pending applications will be rejected.",
            locals: "Project Providers",
            projects: "Projects",
            post: "Post a Project",
            edit: "Edit Project",
            save: "Save Changes",
            posted: "Project posted!",
            updated: "Project updated!",
            closeEdit: "Close project editor",
            viewDetails: "View Details",
            open: "Open",
            closed: "Closed",
            inEscrow: "In Escrow",
            skillsNeeded: "Skills needed",
            timeline: "Timeline",
            deliverables: "Deliverables",
            category: "Category",
            selectCategory: "Select a category",
            categoriesUnavailable: "Categories are unavailable right now.",
            titleField: "Project title*",
            descField: "Description*",
            skillsPlaceholder: "e.g. Graphic Design, Python (comma-separated)",
            addSkill: "Add a required skill",
            timelinePlaceholder: "e.g. 2 weeks, by May 15",
            deliverablesPlaceholder: "What should the student deliver?",
            error: "Title and description are required.",
            postedBy: "Posted by",
            back: "Back to Projects",
            noProjects: "No open projects yet.",
            closeProject: "Close Project",
            deleteProject: "Delete Project",
            deleteConfirm: "Are you sure you want to delete this project? This cannot be undone.",
            bcsLocalProjects: "Provider Projects",
            bcsLocals: "Project Providers",
            noLocals: "No project providers found.",
            providerLoadError: "Failed to load project providers.",
            viewAll: "View all",
          },
          home: {
            marketplaceTitle: "Aggieland's Student and Project Provider Marketplace",
            newPost: "New Post",
            exploreMarketplace: "Explore Local Activity",
            activitySubtitle: "Projects and services from the community",
            filters: "Filters",
            activityTypes: "Activity types",
            showActivityFrom: "Show activity from",
            projectStatus: "Project status",
            localProjects: "Provider Projects",
            localServices: "Local Services",
            viewAllProjects: "View all projects",
            viewAllServices: "View all services",
            filterProjects: "Filter projects by status",
            allProjects: "All projects",
            noMatchingProjects: "No projects match this filter yet.",
            noMatchingActivity: "No local activity matches these filters yet.",
            loadingMarketplace: "Loading…",
            projectsError: "Unable to load projects.",
            servicesError: "Unable to load services.",
            categoriesError: "Service categories are unavailable right now.",
            retry: "Try again",
            sponsorSupport: "Thanks to our sponsors for helping students turn skills into real-world opportunities.",
          },
          projectPost: {
            createPost: "Create Post",
            postAbout: "Post about",
            placeholder: "Share an update...",
            selectType: "Please select a post type.",
            uploadError: "An error occurred during upload.",
            posted: "Posted!",
            addMedia: "Add Media",
            activityFeed: "Project Activity",
            referenceProject: "Reference project",
            noProjectReference: "No project",
            noProjectPosts: "No posts reference this project yet.",
            referencedProject: "Referenced project",
            tagCollaborator: "Tag collaborator",
            with: "with",
            project: "View Project",
            launch: "Launch",
            description: "Description",
            workInProgress: "Work in Progress",
            progressUpdate: "Progress Update",
            achievement: "Achievement",
            collaboration: "Collaboration",
            general: "General Update",
            finalResults: "Final Results",
          },
          adConfirmation: {
            thanks: "Thanks for your submission!",
            msg: "An admin will review your advertisement in 1-3 business days. If it is approved, your ad will appear in the social feed of other users.",
            postAnother: "Post Another Ad",
          },
          ad: {
            approveAds: "Approve Ads",
            approved: "Ad has been approved!",
            rejected: "Ad has been rejected.",
            delete: "You can delete the ad at anytime.",
            sureApprove: "Are you sure you want to approve this ad?",
            sureReject: "Are you sure you want to reject this ad?",
            back: "Go Back",
            approve: "Approve",
            reject: "Reject"
          },
          resetPassword: {
            forgot: "Forgot Password?",
            enterEmail: "Enter your email and we will send you a secure password reset token.",
            sendReset: "Send Token",
            emailVerification: "Email Verification",
            enterCode: "Enter the code we sent to your email",
            verify: "Verify",
            didntReceive: "Didn't receive code?",
            resendCodeIn: "Resend code in ",
            resendCode: "Resend code",
            resetPassword: "Reset Your Password",
            enterPassword: "Enter your new password below to securely reset your password.",
            change: "Change Password"
          },
        }
      },

      es: {
        translation: {
          resumeApplications: resumeApplicationSpanish,
          profileContent: { title: "Contenido del perfil", posts: "Publicaciones", completed: "Proyectos completados", open: "Proyectos abiertos", noCompleted: "Aún no hay proyectos completados.", completedWith: "Completado con {{name}}", reviewInEscrow: "Ver escrow y administrar tu reseña", reviewCount_one: "reseña", reviewCount_other: "reseñas" },
          reviews: { title: "Evalúa tu colaboración", with: "Tu reseña de {{name}}", rating: "Tu calificación", comment: "Tu reseña (opcional)", outOfFive: "{{rating}} de 5", submit: "Enviar reseña", save: "Guardar reseña", saved: "Reseña guardada.", loadError: "No se pudo cargar tu reseña.", saveError: "No se pudo guardar tu reseña. Inténtalo de nuevo.", completedOnly: "Las reseñas están disponibles al completar el escrow." },
          privacy: {
            loadingProfile: "Cargando perfil…", signInToView: "Inicia sesión para ver este perfil.",
            privateProfile: "Este perfil es privado.", profileUnavailable: "Este perfil no está disponible.",
            ownProfileOnly: "Solo puedes ver tu propio perfil.",
          },
          notificationPreferences: {
            title: "Notificaciones por correo", marketplace: "Actualizaciones de escrows e hitos",
            language: "Idioma del correo", enabled: "Recibir actualizaciones por correo", disabled: "No recibir actualizaciones por correo",
            loading: "Cargando preferencias de correo…", loadError: "No se pudieron cargar las preferencias de correo.",
            retry: "Intentar de nuevo", save: "Guardar preferencias de correo", saving: "Guardando…",
            saved: "Preferencias de correo guardadas.", saveError: "No se pudieron guardar las preferencias. Inténtalo de nuevo.",
          },
          reactions: {
            reactions: "Reacciónes",
            like: "Me gusta",
            love: "Me encanta",
            applause: "Aplausos",
            laughing: "Jaja",
            wow: "Wow",
            highfive: "¡Chócala!"
          },
          story: {
            upload: "Subir Historia",
            desc: "Se puede ver las historias para 24 horas hasta que se desaparezcan.",
            choose: "Seleccionar una imagen",
            error: "Sólo están autorizados los ficheros con formato PNG/JPG/JPEG.",
            post: "Subir"
          },
          categories: {
            all: "Todos",
            category: "Categoría",
            general: "General",
            jobs: "Empleos",
            events: "Eventos",
            news: "Noticias",
            newsArticles: "Noticias y Articulos",
            articles: "Articulos",
            local: "Locales",
            us: "Estados Unidos",
            latam: "Latinoamérica",
            global: "Globales",
            tamu: "Texas A&M",
            games: "Partidos de los Aggies",
            advice: "Consejos",
            greatThings: "Cosas geniales",
            construction: "Construcción",
            free: "Artículos grátis",
            home: "Hogar",
            landscape: "Jardínes",
            property: "Alquiler de propiedad",
            vehicle: "Vehículos",
            deals: "Aggie Deals",
          },
          share: {
            create: "Howdy! Hacer nueva publicación...",
            createAd: "Hacer nuevo anuncio...",
            sellItem: "Vender nueva cosa...",
            select: "Elegir Categoría *",
            add: "Seleccionar imagen/video/audio",
            gif: "Agregar GIF",
            flag: "Agregar la bandera de TAMU",
            post: "Subir",
            uploading: "Subiendo...",
            pleaseSelect: "Hay que seleccionar la categoría",
            error: "Sólo están autorizados los videos menos que 60 segundos y los audios menos que 60 segundos.",
            ten: "Sólo se puede elegir 10 fotos, videos, o audios.",
            addMore: "Agregar más +",
            guest: "Howdy! Por favor registra una cuenta para subir posts.",
            url: "Agregar URL",
            confirmationHeading: "Subida realizada correctamente!",
            confirmationText: "Gracias por tu publicación.",
            processingHeading: "Procesando",
            processingText: "Gracias por tu pacienca. Este proceso puede tardar hasta 1 minuto.",
            submitMore: "Subir otro"
          },
          sections: {
            topPosts: "Los Posts más Gustados",
            home: "Inicio",
            discover: "Descubre más",
            friends: "Amigos",
            postAd: "Subir un anuncio",
            shorts: "Videos Cortos",
          },
          common: {
            back: "Volver",
            cancel: "Cancelar",
          },
          post: {
            comment: " Comentario",
            share: "Compartir",
            write: "Escribir nuevo comentario...",
            send: "Subir",
            rate: "calificar este comentario"
          },
          update: {
            welcome: "¡Bienvenidos a Poststation!",
            setupDesc: "Para empezar, vamos a completar tu perfil.",
            start: "¡A empezar!",
            optional: "Todos los campos son opcionales.",
            cover: "Foto de portada",
            profilePic: "Foto de perfil",
            email: "Correo electrónico",
            phone: "Número de teléfono",
            password: "Contraseña",
            name: "Nombre",
            bio: "Bio",
            country: "País/Ciudad",
            language: "Idioma",
            website: "Sitio web",
            link: "URL",
            msg: "Tu perfil es visible para todos los participantes de la plataforma.",
            update: "Actualizar",
            updating: "Actualizando",
            submit: "Cambiar Tu Perfil",
            skills: "Habilidades",
            skillsPlaceholder: "ej. Diseño gráfico, Python, Marketing",
            addSkill: "Agregar",
            university: "Universidad",
            selectUniversity: "Seleccionar universidad",
            major: "Carrera",
            gradYear: "Año de graduación",
            gradYearPlaceholder: "ej. 2026",
            orgName: "Nombre de organización",
            orgNamePlaceholder: "Nombre de organización o negocio",
            orgType: "Tipo de organización",
            selectOrgType: "Seleccionar tipo",
            orgTypeBusiness: "Negocio",
            orgTypeNonprofit: "Organización sin fines de lucro",
            orgTypeResident: "Residente",
          },
          users: {
            build: "Conectar con la Comunidad",
            following: "Seguiendo",
            follow: "Seguir",
            followers: "Seguidores",
            all: "Todos los Usuarios",
            search: "Buscar"
          },
          tamu: {
            tamu: "Universidad Texas A&M",
            desc: "Conectar con otros Aggies.",
            filter: "Filtrar los Posts por Categoría",
            aggieMeals: "Comidas Aggie",
            aggieConsulting: "Consultoria Aggie",
            home: "Inicio",
          },
          meals: {
            title: "Comidas Aggie",
            tagline: "Ofertas semanales para estudiantes en Bryan-College Station",
            time: "Horario:",
            location: "Lugar:",
          },
          consulting: {
            title: "Consultoria Aggie",
            tagline: "Proyectos cortos para estudiantes y experiencia",
            compensation: "Comp:",
            timeframe: "Tiempo:",
            location: "Lugar:",
          },
          otherPages: {
            greatThings: "Compartir contenido divertido y conectar con otros usuarios.",
            news: "Mantente al día de lo que pasa en tu comunidad.",
            filterNews: "Filtrar las Noticias por Categoría",
            market: "Subir anuncios, vender y comprar cosas, y encontrar gangas locales!",
            postAd: "Subir un anuncio que aparezca en el inicio de otros usuarios."
          },
          jobs: {
            desc: "Encontrar trabajo o subir trabajos disponibles en tu negocio.",
            find: "Encontrar trabajo",
            post: "Subir trabajo",
            select: "Elegir",
            filter: "Filtrar los Empleos por Categoría",
            type: "Tipo de trabajo*",
            name: "Nombre de compania*",
            pay: "Salario*",
            schedule: "Horario*",
            location: "Lugar*",
            description: "Descripción",
            contact: "Para solicitar, contáctale a: ",
            gardening: "Jardinería",
            house: "Limpieza de casas",
            janitor: "Servicio de Limpieza",
            office: "Oficina",
            sales: "Vendedores",
            restaurant: "Restaurante",
            students: "Estudiantes",
            professionals: "Profesionales",
            temporary: "Temporales",
            delete: "Quitar trabajo",
            error: "El asterisco (*) significa que el campo es obligatorio."
          },
          notifs: {
            comment: "comentó en tu post.",
            follow: "te siguió!",
            reaction: "reaccionó ",
            topost: "a tu post.",
            notifs: "Notificaciones",
            today: "Hoy",
            week: "Esta semana",
            earlier: "Más temprano",
            escrow_invited: "te invitó a un escrow.",
            student_accepted: "aceptó tu invitación de escrow.",
            student_declined: "rechazó tu invitación de escrow.",
            milestone_added: "añadió un nuevo hito a tu escrow.",
            milestone_approved: "aprobó tu entrega de hito.",
            change_requested: "solicitó cambios en un hito.",
            artifact_submitted: "envió un entregable.",
            escrow_completed: "marcó el escrow como completado.",
            escrow_reopened: "devolvió el escrow para revisión.",
          },
          events: {
            desc: "¡Anuncia tu evento o encuentra planes para esta noche!",
            submit: "Subir Evento",
            name: "Nombre del evento*",
            date: "Fecha*",
            time: "Tiempo*",
            location: "Lugar*",
            description: "Descripción",
            url: "URL",
            upcoming: "Eventos que Vienen",
            error: "Por favor, rellene los siguientes campos: ",
            categ: "Categoría"
          },
          login: {
            login: "Acceso",
            desc: "¡Una plataforma donde la comunidad del Valle del Brazos y la proxima generacion de talento Aggie construyen algo juntos!",
            dont: "¿No tienes cuenta?",
            register: "Registrar",
            username: "Nombre de usuario",
            invalid: "El nombre de usuario o la contraseña está mal",
            error: "No se puede hacer el login!"
          },
          register: {
            desc: "Poststation conecta a estudiantes del Valle Brazos con negocios locales, organizaciones sin fines de lucro y residentes para trabajo por proyectos.",
            do: "¿Ya tienes cuenta?",
            question: "¿Qué tipo de cuenta?",
            student: "Estudiante",
            studentDesc: "Estudiante de Texas A&M o Blinn College que busca experiencia en proyectos reales.",
            local: "Proveedor de Proyectos",
            localDesc: "Persona, empresa u organización que ofrece proyectos a estudiantes.",
            terms: "Aviso: Cuando haces click a `Registrar`, aceptas ",
            privacy: "la Política de Privacidad ",
            use: "los Términos y Condiciones de Uso",
            and: "y",
            back: "Regresar",
            businessName: "Nombre de negocio",
            services: "Servicios",
            skills: "Habilidades",
            selectServices: "Seleccionar servicios",
            servicesUnavailable: "Los servicios no están disponibles en este momento.",
            confirm: "Confirmar la contraseña",
            error: "Se requiere que llenes todos los campos.",
            password: "La contraseña necesita contener al menos 1 letra minúscula, 1 letra mayúscula, 1 dígito y tener entre 8 y 24 caracteres.",
            match: "Las contraseñas no coinciden.",
            email: "Dirección de correo electrónico esta mál.",
            exists: "Ya existe una cuenta con este correo electrónico."
          },
          provider: {
            showPassword: "Mostrar contraseña", hidePassword: "Ocultar contraseña",
            details: "Datos del Proveedor de Proyectos",
            type: "Tipo de proveedor",
            selectType: "Selecciona un tipo de proveedor",
            contactPerson: "Persona de contacto",
            contactPersonPlaceholder: "Nombre completo de la persona responsable de esta cuenta",
            companyName: "Nombre de la empresa",
            companyNamePlaceholder: "Nombre legal o de uso común de la empresa",
            organizationName: "Nombre de la organización",
            organizationNamePlaceholder: "Nombre de la escuela, organización comunitaria o sin fines de lucro",
            contactLabel: "Contacto: {{name}}",
            requiredSetup: "Los datos del proveedor son obligatorios antes de publicar un proyecto.",
            saving: "Guardando…",
            completeProfile: "Completa tu perfil de Proveedor de Proyectos antes de publicar un proyecto.",
            types: { individual: "Persona", company: "Empresa", organization: "Organización" },
            descriptions: { individual: "Una persona que ofrece un proyecto a título personal.", company: "Una empresa comercial que ofrece un proyecto.", organization: "Una organización sin fines de lucro, escuela, grupo comunitario, entidad gubernamental u otra organización no comercial." },
            errors: {
              provider_type_required: "Selecciona Persona, Empresa u Organización.",
              contact_person_required: "El nombre de la persona de contacto es obligatorio.",
              company_name_required: "El nombre de la empresa es obligatorio.",
              organization_name_required: "El nombre de la organización es obligatorio.",
              provider_field_too_long: "El nombre de contacto admite hasta 150 caracteres y el de la entidad hasta 200.",
              provider_fields_conflict: "Introduce solo el nombre de la entidad del tipo de proveedor seleccionado.",
              typeRequired: "Selecciona Persona, Empresa u Organización.",
              contactRequired: "El nombre de la persona de contacto es obligatorio.",
              companyRequired: "El nombre de la empresa es obligatorio.",
              organizationRequired: "El nombre de la organización es obligatorio.",
              saveFailed: "No se pudieron guardar los datos del proveedor. Inténtalo de nuevo."
            }
          },
          admin: {
            dashboard: "Panel de Administración",
            users: "Usuarios",
            escrows: "Escrows",
            allTypes: "Todos los Tipos",
            allStatuses: "Todos los Estados",
            name: "Nombre",
            email: "Correo",
            type: "Tipo",
            joined: "Registrado",
            project: "Proyecto",
            student: "Estudiante",
            local: "Proveedor de Proyectos",
            status: "Estado",
            lastUpdated: "Última Actualización",
            escrowLock: "Crear Match (Escrow Lock)",
            selectStudent: "Seleccionar estudiante",
            selectProject: "Seleccionar proyecto",
            lockBtn: "Crear Escrow",
            lockSuccess: "Escrow creado correctamente.",
            lockError: "Error al crear el escrow.",
            noUsers: "No se encontraron usuarios.",
            noEscrows: "No se encontraron escrows.",
          },
          escrow: {
            milestones: "Hitos", activity: "Actividad", toggleActivity: "Mostrar u ocultar la actividad", noMilestones: "Aún no hay hitos.", milestoneLoadError: "No se pudieron cargar los hitos.",
            accept: "Aceptar Invitación",
            decline: "Rechazar",
            pendingMsg: "Has sido invitado a este proyecto. Acepta para comenzar el trabajo.",
            invite: "Invitar a Proyecto",
            inviteTitle: "Invitar Estudiante",
            selectProject: "Selecciona uno de tus proyectos abiertos",
            noProjects: "No tienes proyectos abiertos. Crea un proyecto primero.",
            inviteSuccess: "¡Invitación enviada! Escrow creado.",
            inviteError: "Error al crear el escrow.",
            confirm: "Enviar Invitación",
            myEscrows: "Mis Escrows",
            noEscrows: "No hay escrows aún.",
            status: "Estado",
            pending: "Pendiente",
            active: "Activo",
            submitted: "Enviado",
            completed: "Completado",
            cancelled: "Cancelado",
            project: "Proyecto",
            student: "Estudiante",
            local: "Proveedor de Proyectos",
            lastUpdated: "Última actualización",
            submitDeliverable: "Enviar Entregable",
            descriptionPlaceholder: "Describe tu entregable...",
            submitBtn: "Enviar Trabajo",
            submitting: "Enviando...",
            approve: "Aprobar y Completar",
            requestChanges: "Solicitar Cambios",
            artifact: "Trabajo Enviado",
            timeline: "Historial de Estados",
            backToEscrows: "Volver a Mis Escrows",
            viewProject: "Ver Proyecto",
            submitError: "Por favor agrega una descripción antes de enviar.",
            submitSuccess: "Trabajo enviado correctamente.",
            noArtifact: "Aún no hay entregable.",
          },
          services: {
            category: "Categoría",
            selectCategory: "Seleccionar una categoría",
            titleField: "Título del servicio*",
            descField: "Descripción*",
            skillsPlaceholder: "Habilidades que cubre",
            availabilityField: "Disponibilidad",
            availabilityPlaceholder: "ej. Fines de semana, 10 hrs/semana",
            error: "El título y la descripción son obligatorios.",
            post: "Publicar Servicio",
            edit: "Editar Servicio",
            save: "Guardar cambios",
            posted: "¡Servicio publicado!",
            updated: "¡Servicio actualizado!",
          },
          talent: {
            browse: "Ver Talento",
            studentTalent: "Talento Estudiantil",
            studentServices: "Servicios Estudiantiles",
            students: "Estudiantes",
            services: "Servicios",
            searchPlaceholder: "Buscar por habilidad o nombre...",
            filterServices: "Filtrar servicios por categoría",
            allCategories: "Todas las categorías",
            categoriesSelected: "{{count}} categorías seleccionadas",
            scrollCategoriesLeft: "Desplazar categorías a la izquierda",
            scrollCategoriesRight: "Desplazar categorías a la derecha",
            loading: "Cargando…", loadError: "No se pudieron cargar los estudiantes.",
            noStudents: "No se encontraron estudiantes.",
            noServices: "No hay servicios publicados aún.",
            noServicesInCategory: "Aún no hay servicios en esta categoría.",
            postService: "Publicar Servicio",
            editService: "Editar Servicio",
            viewProfile: "Ver Perfil",
            deleteService: "Eliminar Servicio",
            deleteConfirm: "¿Estás seguro de que deseas eliminar este servicio? Esta acción no se puede deshacer.",
          },
          projects: {
            browse: "Ver Proyectos",
            all: "Todos", loading: "Cargando…", loadError: "No se pudieron cargar los proyectos.", notFound: "No se encontró el proyecto.",
            viewProviderProjects: "Ver proyectos", clearProvider: "Mostrar todos los proveedores", providerFilter: "Proyectos de {{name}}", closeConfirm: "¿Cerrar este proyecto? Se rechazarán las solicitudes pendientes.",
            locals: "Proveedores de Proyectos",
            projects: "Proyectos",
            post: "Publicar Proyecto",
            edit: "Editar Proyecto",
            save: "Guardar cambios",
            posted: "¡Proyecto publicado!",
            updated: "¡Proyecto actualizado!",
            closeEdit: "Cerrar editor de proyecto",
            viewDetails: "Ver Detalles",
            open: "Abierto",
            closed: "Cerrado",
            inEscrow: "En Escrow",
            skillsNeeded: "Habilidades requeridas",
            timeline: "Plazo",
            deliverables: "Entregables",
            category: "Categoría",
            selectCategory: "Seleccionar una categoría",
            categoriesUnavailable: "Las categorías no están disponibles en este momento.",
            titleField: "Título del proyecto*",
            descField: "Descripción*",
            skillsPlaceholder: "ej. Diseño gráfico, Python (separados por coma)",
            addSkill: "Agregar una habilidad requerida",
            timelinePlaceholder: "ej. 2 semanas, antes del 15 de mayo",
            deliverablesPlaceholder: "¿Qué debe entregar el estudiante?",
            error: "El título y la descripción son obligatorios.",
            postedBy: "Publicado por",
            back: "Volver a Proyectos",
            noProjects: "Aún no hay proyectos abiertos.",
            closeProject: "Cerrar Proyecto",
            deleteProject: "Eliminar Proyecto",
            deleteConfirm: "¿Estás seguro de que deseas eliminar este proyecto? Esta acción no se puede deshacer.",
            bcsLocalProjects: "Proyectos de Proveedores",
            bcsLocals: "Proveedores de Proyectos",
            noLocals: "No se encontraron proveedores de proyectos.",
            providerLoadError: "No se pudieron cargar los proveedores de proyectos.",
            viewAll: "Ver todo",
          },
          home: {
            marketplaceTitle: "Mercado de Estudiantes y Proveedores de Proyectos de Aggieland",
            newPost: "Nueva Publicación",
            exploreMarketplace: "Explora la actividad local",
            activitySubtitle: "Proyectos y servicios de la comunidad",
            filters: "Filtros",
            activityTypes: "Tipos de actividad",
            showActivityFrom: "Mostrar actividad de",
            projectStatus: "Estado del proyecto",
            localProjects: "Proyectos de proveedores",
            localServices: "Servicios locales",
            viewAllProjects: "Ver todos los proyectos",
            viewAllServices: "Ver todos los servicios",
            filterProjects: "Filtrar proyectos por estado",
            allProjects: "Todos los proyectos",
            noMatchingProjects: "No hay proyectos que coincidan con este filtro.",
            noMatchingActivity: "No hay actividad local que coincida con estos filtros.",
            loadingMarketplace: "Cargando…",
            projectsError: "No se pudieron cargar los proyectos.",
            servicesError: "No se pudieron cargar los servicios.",
            categoriesError: "Las categorías de servicios no están disponibles en este momento.",
            retry: "Intentar de nuevo",
            sponsorSupport: "Gracias a nuestros patrocinadores por ayudar a estudiantes a convertir sus habilidades en oportunidades reales.",
          },
          projectPost: {
            createPost: "Crear Publicación",
            postAbout: "Publicar sobre",
            placeholder: "Comparte una actualización...",
            selectType: "Por favor selecciona el tipo de publicación.",
            uploadError: "Ocurrió un error durante la subida.",
            posted: "¡Publicado!",
            addMedia: "Agregar Media",
            activityFeed: "Actividad del Proyecto",
            referenceProject: "Referenciar proyecto",
            noProjectReference: "Sin proyecto",
            noProjectPosts: "Aún no hay publicaciones que hagan referencia a este proyecto.",
            referencedProject: "Proyecto referenciado",
            tagCollaborator: "Etiquetar colaborador",
            with: "con",
            project: "Ver Proyecto",
            launch: "Lanzamiento",
            description: "Descripción",
            workInProgress: "En Progreso",
            progressUpdate: "Actualización de Progreso",
            achievement: "Logro",
            collaboration: "Colaboración",
            general: "Actualización General",
            finalResults: "Resultados Finales",
          },
          adConfirmation: {
            thanks: "¡Gracias por tu anuncio!",
            msg: "Vamos a revisar tu anuncio en 1-3 días hábiles. Si lo aprobamos, tu anuncio se irá para el feed de otros usuarios.",
            postAnother: "Subir Más",
          },
          ad: {
            approveAds: "Aprobar los Anuncios",
            approved: "Has aprobado el anuncio!",
            rejected: "Has rechazado el anuncio.",
            delete: "Puedes borrar el anuncio en cada momento.",
            sureApprove: "¿Quieres aprobar este anuncio?",
            sureReject: "¿Quieres rechazar este anuncio?",
            back: "Volver atrás",
            approve: "Aprobar",
            reject: "Rechazar"
          },
          resetPassword: {
            forgot: "¿Olvidaste tu contraseña?",
            enterEmail: "Escribe tu correo electronico y te vamos a enviar un código seguro para cambiar tu contraseña ",
            sendReset: "Enviar Código",
            emailVerification: "Verificación de Email",
            enterCode: "Escribe el código que te enviamos a tu correo electrónico",
            verify: "Verificar",
            didntReceive: "¿No recibiste el código?",
            resendCodeIn: "Reenviar código en",
            resendCode: "Reenviar código",
            resetPassword: "Cambiar tu Contraseña",
            enterPassword: "Escribe tu nueva contraseña para cambiarlo.",
            change: "Cambiar Contraseña"
          }
        },
        shorts: {
          desc: "Subir y ver videos cortos para reírse y quedarse actualizada hoy en dia.",
          post: "Subir un video más corto que 30 segundos.",
          caption: "Escribir título...",
          guest: "Howdy! Para subir videos cortos, por favor hacer el login o abra una cuenta.",
        },
      }
    }
  });


export default i18n;
